import { Action, Update } from 'history';
import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTrackPageView } from '../hooks/tracking/useTrackingEvents';
import useContext from '../hooks/useContext';
import { PreviewContext } from '../preview/PreviewProvider';
import useHistory from '../Router/useHistory';
import { ComponentLoaderProvider, IComponentList } from '../utils/componentLoader';
import useHashListener from './useHashListener';

export const SiteContext = createContext<ISiteContextValue | undefined>(undefined);

const buildErrorContext = (context: any) => ({
  ...context,
  cells: {
    ...context.cells,
    main: {
      ...context.cells.main,
      component: 'pages/ErrorPage',
    },
  },
});

const buildRequestUrl = (pathname: string, queryString?: string) => {
  if (!queryString) {
    return `${pathname}?o=json`;
  }

  return `${pathname}${queryString}&o=json`;
};

interface ISite {
  children: React.ReactNode;
  siteContext: ISiteContext;
  preloadedComponents?: IComponentList;
}

interface ILocationState {
  reload?: boolean;
}

const Site = ({ siteContext, children, preloadedComponents }: ISite) => {
  const { utcDateString } = useContext(PreviewContext);
  const [ctx, setCtx] = useState(siteContext);
  const [loading, setLoading] = useState(false);
  const { listen, location } = useHistory();
  const stack = useRef([{ key: location?.key, data: siteContext }]);
  const trackPageView = useTrackPageView();
  const hashState = useHashListener();

  useEffect(() => {
    listen(({ location: { pathname, search, state, key, hash }, action }: Update) => {
      // Hack to prevent press page from going back in browser
      if (hash?.substring(0, 2) === '#/') {
        return;
      }

      if (hashState.hashChange) {
        return;
      }

      const goBack =
        action === Action.Pop &&
        stack.current.length > 1 &&
        stack.current[stack.current.length - 2].key === key;

      if (goBack) {
        stack.current.splice(stack.current.length - 1, 1);
        const nextContext = stack.current[stack.current.length - 1].data;
        trackPageView(nextContext.data.meta?.title);
        setCtx(nextContext);
        return;
      }

      const goForward =
        action === Action.Pop &&
        stack.current.length > 1 &&
        stack.current[stack.current.length - 2].key !== key;

      const load =
        action === Action.Push ||
        (action === Action.Replace && (state as ILocationState)?.reload) ||
        (action === Action.Pop && stack.current.length <= 1);

      // TODO, sitecontext is not aware of any ErrorPage, put this
      // outside of sitecontext
      const onError = (error?: Error) => {
        const context = buildErrorContext(ctx);
        setCtx(context);
        stack.current.push({ key, data: context });

        if (error) {
          console.error(error);
        }
      };

      if (load || goForward) {
        setLoading(true);
        fetch(buildRequestUrl(pathname, search), {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Preview-Date': utcDateString || '',
          },
        })
          .then((response) => {
            if (response.status !== 404 && (response.status < 200 || response.status >= 300)) {
              onError();
            }
            response
              .json()
              .then((context) => {
                window.scrollTo(0, 0);
                trackPageView(context.data.meta?.title);

                if (action !== Action.Pop) {
                  stack.current.push({ key, data: context });
                }

                setCtx(context);
              })
              .catch(onError);
          })
          .catch(onError)
          .finally(() => setLoading(false));
      }
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      ...ctx,
      index: stack.current.length,
      loading,
    }),
    [ctx, loading],
  );

  return (
    <ComponentLoaderProvider components={preloadedComponents}>
      <SiteContext.Provider value={contextValue}>
        {React.Children.only(children)}
      </SiteContext.Provider>
    </ComponentLoaderProvider>
  );
};

export default Site;
